import {AxiosError, HttpStatusCode} from 'axios';
import {uniq, uniqBy} from 'lodash';
import {createFCUploadFileSessionId} from '../../api/EvidenceSourceUpload';
import {
  cancelFileTransfer,
  checkTransferStatus,
  deleteRemoteFile,
  getConnectionKeyPostRequest,
  getJobIdForMultipleUpload,
  getJobIdForSingleUpload,
  getLocalFile,
} from '../../api/FCUploadFileServices';
import {
  UploadMethod,
  UploadSessionData,
} from '../../types/upload-new-eviidence/evidenceSourceTypes';
import {
  FCAPIErrorResponse,
  FCFileGridItem,
  FCFileTransfer,
  FCFileTransferSession,
  FCLocalDirectory,
  FCUploadFileTaskParams,
} from '../../types/upload-new-eviidence/fcUploadFileTypes';
import {
  convertPathToSlashes,
  FC_UPLOAD_STATIC_PATH,
  FCFileTransferInternalState,
  FCFileTransferState,
  getFileNameFromRemotePath,
  getMimeTypeFromFileName,
  getRelativePathIfMatching,
  getStoragePathParam,
  isRemotePathAndLocalPathEqual,
} from '../../utils/fcUploadFileUtils';
import {isEmptyInstance} from '../../utils/utils';

class FCUploadFileTask {
  uploadTaskId: string;
  fcEncryptedPayload: string;
  projectAlphaCode: string;
  clientId: string;
  projectId: string;
  evidenceSourceSessionId: string;

  remoteDirectoryBasePath: string;

  fileTransferSession: Partial<FCFileTransferSession> = {};
  fileTransferState: FCFileTransferState = FCFileTransferState.PENDING;
  fileTransferInternalState: FCFileTransferInternalState =
    FCFileTransferInternalState.INITTASK;

  onUpdatePendingFileArray?: (
    taskId: string,
    fileGridItemArray: FCFileGridItem[]
  ) => void;

  onUpdateUploadedFileArray?: (
    taskId: string,
    fileGridItemArray: FCFileGridItem[]
  ) => void;

  onTransferCompleted?: (taskId: string) => void;
  onTransferFailed?: (taskId: string) => void;
  onFCError?: (error: AxiosError) => void;

  abortController: AbortController = new AbortController();

  pendingFileGridItemArray: FCFileGridItem[] = [];
  uploadedFileGridItemArray: FCFileGridItem[] = [];

  isUploadTaskCompleted = false;
  isUploadTaskCancelled = false;
  isRemoteFileDeleted = false;

  constructor(uploadFileTaskParams: FCUploadFileTaskParams) {
    const {
      uploadTaskId,
      fcEncryptedPayload,
      projectAlphaCode,
      clientId,
      projectId,
      evidenceSourceSessionId,
      localFile,
      onUpdatePendingFileArray,
      onUpdateUploadedFileArray,
      onTransferCompleted,
      onTransferFailed,
      onFCError,
    } = uploadFileTaskParams;

    this.uploadTaskId = uploadTaskId;
    this.fcEncryptedPayload = fcEncryptedPayload;

    this.projectAlphaCode = projectAlphaCode;

    this.clientId = clientId;
    this.projectId = projectId;
    this.evidenceSourceSessionId = evidenceSourceSessionId;

    this.remoteDirectoryBasePath =
      isEmptyInstance(this.projectAlphaCode) &&
      isEmptyInstance(this.evidenceSourceSessionId)
        ? ''
        : `/${this.projectAlphaCode}/${this.evidenceSourceSessionId}/${FC_UPLOAD_STATIC_PATH}`;

    this.fileTransferSession = {
      ...this.fileTransferSession,
      localFile: {...localFile},
    };

    this.onUpdatePendingFileArray = onUpdatePendingFileArray;
    this.onUpdateUploadedFileArray = onUpdateUploadedFileArray;
    this.onTransferCompleted = onTransferCompleted;
    this.onTransferFailed = onTransferFailed;
    this.onFCError = onFCError;

    this.setPendingLocalFile();
  }

  setPendingLocalFile() {
    const {localFile} = this.fileTransferSession;
    this.pendingFileGridItemArray = [
      {
        id: `${this.uploadTaskId}-${localFile?.path}`,
        displayName: `${localFile?.name ?? ''}`,
        status: FCFileTransferState.PENDING,
        progress: 0,
        cancelTransferOnline: true,
        name: `${localFile?.name ?? ''}`,
        size: localFile?.size ?? 0,
        uploadTaskId: this.uploadTaskId,
        remoteDirectory: this.remoteDirectoryBasePath,
      },
    ];
    this.onUpdatePendingFileArray?.(
      this.uploadTaskId,
      this.pendingFileGridItemArray
    );
  }

  connectWithFC() {
    return new Promise((resolve) => {
      if (
        this.fileTransferInternalState === FCFileTransferInternalState.CONNECTED
      ) {
        resolve(true);
      } else {
        this.fileTransferInternalState = FCFileTransferInternalState.CONNECTING;
        getConnectionKeyPostRequest(this.fcEncryptedPayload, {
          shouldRetry: true,
          onRetry: () => {
            this.updatePendingGridItemToReconnectingStatus();
            resolve(false);
          },
        })
          .then((connectionKeyData) => {
            if (connectionKeyData.status === HttpStatusCode.Ok) {
              const {connectionKey} = connectionKeyData?.data as {
                connectionKey: string;
              };
              this.fileTransferSession = {
                ...this.fileTransferSession,
                connectionKey,
              };
              this.getJobIdForFile().then((success) => {
                resolve(success);
              });
            } else {
              this.setFailState();
              this.fileTransferInternalState =
                FCFileTransferInternalState.CONNECTION_FAIL;
              resolve(false);
            }
          })
          .catch((e) => {
            console.error(`connectWithFC: `, e);
            this.fileTransferInternalState =
              FCFileTransferInternalState.CONNECTION_FAIL;
            this.setFailState();
            this.onFCError?.(e);
            resolve(false);
          });
      }
    });
  }

  getJobIdForFile() {
    const {connectionKey = '', localFile} = this.fileTransferSession;
    return new Promise((resolve) => {
      ((localFile?.isFile ?? false)
        ? getJobIdForSingleUpload(
            connectionKey,
            this.remoteDirectoryBasePath,
            localFile?.path ?? '',
            {
              shouldRetry: true,
              onRetry: () => {
                this.updatePendingGridItemToReconnectingStatus();
                resolve(false);
              },
            }
          )
        : getJobIdForMultipleUpload(
            connectionKey,
            this.remoteDirectoryBasePath,
            [localFile?.path ?? ''],
            {
              shouldRetry: true,
              onRetry: () => {
                this.updatePendingGridItemToReconnectingStatus();
                resolve(false);
              },
            }
          )
      )
        .then((data) => {
          if (data.status === HttpStatusCode.Ok) {
            this.fileTransferInternalState =
              FCFileTransferInternalState.CONNECTED;
            this.fileTransferState = FCFileTransferState.PENDING;
            const {jobId = ''} = data?.data as {
              jobId: string;
            };
            this.fileTransferSession = {
              ...this.fileTransferSession,
              jobId,
            };
            resolve(true);
          } else {
            this.setFailState();
            this.fileTransferInternalState =
              FCFileTransferInternalState.CONNECTION_FAIL;
            resolve(false);
          }
        })
        .catch((e) => {
          console.error(`getJobIdForFile: `, e);
          this.setFailState();
          this.fileTransferInternalState =
            FCFileTransferInternalState.CONNECTION_FAIL;
          this.onFCError?.(e);
          resolve(false);
        });
    });
  }

  getJobIdStatus() {
    const {
      jobId,
      transferCancelled = false,
      transferComplete = false,
      localFile,
    } = this.fileTransferSession;
    return new Promise((resolve) => {
      if (
        !isEmptyInstance(jobId) &&
        !this.isUploadTaskCompleted &&
        !this.isUploadTaskCancelled &&
        !(transferCancelled || transferComplete)
      ) {
        checkTransferStatus(jobId, this.abortController?.signal, {
          shouldRetry: !this.isUploadTaskCancelled,
          onRetry: () => {
            if (!this.isUploadTaskCancelled) {
              this.fileTransferState = FCFileTransferState.RECONNECTING;
              this.updatePendingGridItemToCustomStatus(
                FCFileTransferState.RECONNECTING
              );
            }
            resolve(false);
          },
        })
          .then((response) => {
            if (response.status === HttpStatusCode.Ok) {
              const {
                fileTransfers = [],
                completedFiles = [],
                transferCancelled,
                transferComplete,
                transferID,
                percent,
                sizeAllFiles,
                transferErrors = [],
              } = response.data;

              this.fileTransferSession = {
                ...this.fileTransferSession,
                fileTransfers,
                completedFiles,
                transferID,
                transferCancelled,
                transferComplete,
                percent,
                sizeAllFiles,
                uploadTaskId: this.uploadTaskId,
                remoteFileNames: new Set([
                  ...fileTransfers
                    .filter(
                      (item: FCFileTransfer) =>
                        !isEmptyInstance(
                          item?.remoteFilename || item?.currentFilename
                        )
                    )
                    .map(
                      (item: FCFileTransfer) =>
                        item?.remoteFilename || item?.currentFilename
                    ),
                  ...Array.from(
                    this.fileTransferSession?.remoteFileNames ?? []
                  ),
                ]),
                localFileNameSizeMap: new Map([
                  ...(this.fileTransferSession?.localFileNameSizeMap ??
                    new Map()),
                  ...fileTransfers
                    .filter(
                      (item: FCFileTransfer) =>
                        !isEmptyInstance(
                          item?.remoteFilename || item?.currentFilename || ''
                        )
                    )
                    .map((fileTransfer: FCFileTransfer) => [
                      fileTransfer?.remoteFilename ||
                        fileTransfer?.currentFilename ||
                        '',
                      localFile?.isFile
                        ? (localFile?.size ?? 0)
                        : fileTransfer?.sizeCurrentFile ||
                          fileTransfer?.sizeAllFiles ||
                          0,
                    ]),
                ]),
              };

              this.updatePendingGridItem();
              this.updateUploadedGridItem();

              !isEmptyInstance(transferErrors) &&
                this.setUploadStatusAPIError(
                  (transferErrors ?? [])
                    .map((error: Record<string, string>) => error?.error ?? '')
                    .join(' ')
                );

              if (transferCancelled || transferComplete) {
                this.updateUploadedGridItemFileSize();
                this.isUploadTaskCompleted = true;
                this.onTransferCompleted?.(this.uploadTaskId);
                this.fileTransferState = FCFileTransferState.COMPLETED;
              }

              if (
                isEmptyInstance(transferErrors) &&
                !(transferCancelled || transferComplete)
              ) {
                this.fileTransferState = FCFileTransferState.UPLOADING;
              }
              resolve(false);
            } else {
              this.setFailState();
              resolve(false);
            }
          })
          .catch((e) => {
            console.error(`getJobIdStatus: `, e);
            this.setFailState();
            this.onFCError?.(e);
            resolve(false);
          });
      } else {
        resolve(true);
      }
    });
  }

  setUploadStatusAPIError(errorMessage: string) {
    const customError: FCAPIErrorResponse = {
      reason: {text: errorMessage},
    };

    !isEmptyInstance(errorMessage) &&
      this.onFCError?.(
        new AxiosError(errorMessage, 'ERR_CODE', {} as never, null, {
          data: customError,
        } as never)
      );
  }

  updatePendingGridItem() {
    const {
      jobId = '',
      transferID,
      fileTransfers = [],
      localFile,
      percent = 0,
      sizeAllFiles = 0,
      transferComplete = false,
      transferCancelled = false,
    } = this.fileTransferSession;

    const currentLocalPendingDirArray = [
      ...((localFile?.isDirectory ?? false) &&
      !(transferComplete || transferCancelled)
        ? [
            {
              displayName: `${localFile?.name ?? ''}`,
              id: `${this.uploadTaskId}-${localFile?.path ?? ''}`,
              name: `${this.remoteDirectoryBasePath}/${localFile?.name ?? ''}`,
              status: FCFileTransferState.UPLOADING,
              size: sizeAllFiles,
              progress: percent,
              cancelTransferOnline: true,
              localDirectoryPath: localFile?.path ?? '',
              jobId,
              remoteFilename: `${this.remoteDirectoryBasePath}/${localFile?.name ?? ''}`,
              transferID,
              uploadTaskId: this.uploadTaskId,
              remoteDirectory: this.remoteDirectoryBasePath,
            },
          ]
        : []),

      ...((localFile?.isFile ?? false) &&
      !(transferComplete || transferCancelled) &&
      isEmptyInstance(
        (fileTransfers ?? []).filter(
          (item) =>
            !isEmptyInstance(item?.localFilename) &&
            !isEmptyInstance(item?.remoteFilename)
        )
      )
        ? [
            {
              id: `${this.uploadTaskId}-${localFile?.path}`,
              status: FCFileTransferState.PENDING,
              progress: 0,
              cancelTransferOnline: true,
              name: `${localFile?.name ?? ''}`,
              displayName: `${localFile?.name ?? ''}`,
              size: localFile?.size ?? 0,
              uploadTaskId: this.uploadTaskId,
              remoteDirectory: this.remoteDirectoryBasePath,
            },
          ]
        : []),
    ];

    this.pendingFileGridItemArray = uniqBy(
      [
        ...currentLocalPendingDirArray,
        ...((localFile?.isFile ?? false)
          ? uniqBy(
              (fileTransfers ?? [])
                .filter(
                  (item) =>
                    !isEmptyInstance(item?.localFilename) &&
                    !isEmptyInstance(item?.remoteFilename)
                )
                ?.map(
                  ({
                    sizeCurrentFile = 0,
                    currentPercent = 0,
                    remoteFilename = '',
                    localFilename = '',
                  }: FCFileTransfer) => ({
                    id: `${this.uploadTaskId}-${convertPathToSlashes(localFilename)}`,
                    displayName: `${localFile?.name ?? ''}`,
                    name: remoteFilename,
                    status: FCFileTransferState.UPLOADING,
                    size: sizeCurrentFile,
                    progress: currentPercent,
                    cancelTransferOnline: true,
                    localDirectoryPath: convertPathToSlashes(localFilename),
                    jobId,
                    remoteFilename,
                    transferID,
                    uploadTaskId: this.uploadTaskId,
                    remoteDirectory: this.remoteDirectoryBasePath,
                  })
                ) ?? [],
              'remoteFilename'
            )
          : []),
      ],
      'id'
    );

    this.onUpdatePendingFileArray?.(
      this.uploadTaskId,
      this.pendingFileGridItemArray.slice()
    );
  }

  updateUploadedGridItem() {
    const {
      jobId,
      completedFiles = [],
      remoteFileNames = new Set([]),
      localFile,
      localFileNameSizeMap = new Map([]),
      transferCancelled = false,
      transferComplete = false,
    } = this.fileTransferSession;

    if (!isEmptyInstance(uniq(completedFiles))) {
      const updatedCompletedFileArray: string[] = uniq(completedFiles).map(
        (item) => convertPathToSlashes(item)
      );

      const localFileArray: FCLocalDirectory[] = isEmptyInstance(localFile)
        ? []
        : [localFile];

      const completedLocalFileArray: FCLocalDirectory[] = localFileArray.filter(
        (item) =>
          updatedCompletedFileArray.includes(item?.id ?? '') &&
          !item?.isDirectory
      );

      const completedLocalDirArray: FCLocalDirectory[] = localFileArray.filter(
        (item) => item?.isDirectory
      );

      const completedFileForLocalDirArray: string[] =
        updatedCompletedFileArray.filter(
          (item) =>
            !localFileArray.some(
              (file: FCLocalDirectory) => file?.path === item
            )
        );

      const remoteFileNameArray = Array.from(remoteFileNames);

      const compareRemotePathAndLocalPath = (remoteFileName: string) =>
        uniq(updatedCompletedFileArray).some((completedFile: string) =>
          isRemotePathAndLocalPathEqual(
            this.remoteDirectoryBasePath,
            remoteFileName,
            completedFile
          )
        );

      const pastUploadedFileArray: FCFileGridItem[] =
        this.uploadedFileGridItemArray.filter(
          (item: FCFileGridItem) => !isEmptyInstance(item?.isUploading)
        );

      const completedRemoteFileArray = remoteFileNameArray.filter(
        compareRemotePathAndLocalPath
      );

      const completedLocalFileHavingRemotePathArray: FCLocalDirectory[] =
        completedLocalFileArray
          .filter((item: FCLocalDirectory) =>
            remoteFileNameArray.includes(
              `${this.remoteDirectoryBasePath}/${item?.name}`
            )
          )
          .map((item: FCLocalDirectory) => {
            return {
              ...item,
              remoteFilename:
                completedRemoteFileArray.find((remoteFileName: string) =>
                  isRemotePathAndLocalPathEqual(
                    this.remoteDirectoryBasePath,
                    remoteFileName,
                    item?.path ?? ''
                  )
                ) ?? '',
            };
          });

      const latestUploadedFileArray: FCFileGridItem[] =
        completedLocalFileHavingRemotePathArray.map((item) => {
          return {
            id: `${this.uploadTaskId}-${item?.remoteFilename ?? ''}`,
            status: FCFileTransferState.COMPLETED,
            size:
              localFileNameSizeMap.get(item?.remoteFilename ?? '') ??
              item?.size ??
              0,
            progress: 100,
            cancelTransferOnline: false,
            displayName: item?.name ?? '',
            name: item?.remoteFilename ?? '',
            jobId,
            remoteFilename: item?.remoteFilename ?? '',
            isUploading: false,
            uploadTaskId: this.uploadTaskId,
            remoteDirectory: this.remoteDirectoryBasePath,
            localDirectoryPath: item?.path ?? '',
          };
        });

      const woRemoteFileNameUploadedFileArray = completedLocalFileArray
        .filter(
          (item: FCLocalDirectory) =>
            !remoteFileNameArray.includes(
              `${this.remoteDirectoryBasePath}/${item?.name}`
            )
        )
        .map((item: FCLocalDirectory) => {
          const remoteFilename = `${this.remoteDirectoryBasePath}/${item?.name}`;
          return {
            id: `${this.uploadTaskId}-${remoteFilename}`,
            status: FCFileTransferState.COMPLETED,
            size: localFileNameSizeMap.get(remoteFilename) ?? item?.size ?? 0,
            progress: 100,
            cancelTransferOnline: false,
            name: remoteFilename,
            displayName: `${item?.name ?? ''}`,
            jobId,
            remoteFilename,
            isUploading: false,
            uploadTaskId: this.uploadTaskId,
            localDirectoryPath: item?.path ?? '',
            remoteDirectory: this.remoteDirectoryBasePath,
          };
        });

      const remoteFileNameForDirFileArray: string[] =
        remoteFileNameArray.filter((item) =>
          completedFileForLocalDirArray.some((completedFile: string) =>
            isRemotePathAndLocalPathEqual(
              this.remoteDirectoryBasePath,
              item,
              completedFile
            )
          )
        );

      const latestUploadedRestFileArray: FCFileGridItem[] =
        completedFileForLocalDirArray
          .map((completedFile: string) => {
            const remoteFilename = remoteFileNameForDirFileArray.find((item) =>
              isRemotePathAndLocalPathEqual(
                this.remoteDirectoryBasePath,
                item,
                completedFile
              )
            );
            return {
              id: `${this.uploadTaskId}-${remoteFilename}`,
              status: FCFileTransferState.COMPLETED,
              progress: 100,
              cancelTransferOnline: false,
              name: remoteFilename ?? '',
              displayName: (remoteFilename ?? '').replace(
                `${this?.remoteDirectoryBasePath ?? ''}/`,
                ''
              ),
              size: localFileNameSizeMap.get(remoteFilename ?? '') ?? 0,
              jobId,
              localDirectoryPath: completedFile,
              isUploading: false,
              remoteFilename,
              uploadTaskId: this.uploadTaskId,
              remoteDirectory: this.remoteDirectoryBasePath,
            };
          })
          .filter((item) => !isEmptyInstance(item?.name));

      const completedFileWOFileTransferObjArray: FCFileGridItem[] =
        completedFileForLocalDirArray
          .filter(
            (completedFile: string) =>
              !remoteFileNameArray.some((item: string) =>
                isRemotePathAndLocalPathEqual(
                  this.remoteDirectoryBasePath,
                  item,
                  completedFile
                )
              )
          )
          .map((completedFile: string) => {
            const remoteFilename = `${this.remoteDirectoryBasePath}${getRelativePathIfMatching(
              completedFile,
              completedLocalDirArray.find(
                (item) =>
                  !isEmptyInstance(
                    getRelativePathIfMatching(completedFile, item?.path)
                  )
              )?.path ?? ''
            )}`;

            return {
              id: `${this.uploadTaskId}-${remoteFilename}`,
              status: FCFileTransferState.COMPLETED,
              progress: 100,
              cancelTransferOnline: false,
              name: remoteFilename,
              displayName: (remoteFilename ?? '').replace(
                `${this?.remoteDirectoryBasePath ?? ''}/`,
                ''
              ),
              size: localFileNameSizeMap.get(remoteFilename) ?? 0,
              jobId,
              localDirectoryPath: completedFile,
              isUploading: false,
              sizeNotAvailable: true,
              remoteFilename,
              uploadTaskId: this.uploadTaskId,
              remoteDirectory: this.remoteDirectoryBasePath,
            };
          });

      this.uploadedFileGridItemArray = [
        ...pastUploadedFileArray,
        ...latestUploadedFileArray,
        ...latestUploadedRestFileArray,
        ...woRemoteFileNameUploadedFileArray,
        ...completedFileWOFileTransferObjArray,
      ];

      this.uploadedFileGridItemArray = uniqBy(
        uniqBy(this.uploadedFileGridItemArray, 'id'),
        'remoteFilename'
      );

      if (!(transferComplete || transferCancelled)) {
        this.onUpdateUploadedFileArray?.(
          this.uploadTaskId,
          this.uploadedFileGridItemArray
        );
        this.updateUploadedGridItemFileSize();
      }
    }
  }

  updateUploadedGridItemFileSize() {
    if (
      this.uploadedFileGridItemArray.some(
        (file: FCFileGridItem) => file?.sizeNotAvailable
      )
    ) {
      const localFileNameSizeMap: Map<string, number> = new Map();

      Promise.allSettled(
        this.uploadedFileGridItemArray
          .filter((file: FCFileGridItem) => file?.sizeNotAvailable)
          .map(async (file: FCFileGridItem) => {
            const localFile = await getLocalFile(
              file?.localDirectoryPath ?? ''
            );
            const fileSize = localFile?.data?.size ?? 0;
            localFileNameSizeMap.set(file?.remoteFilename ?? '', fileSize ?? 0);
          })
      ).then(() => {
        this.uploadedFileGridItemArray = uniqBy(
          uniqBy(
            [
              ...this.uploadedFileGridItemArray.filter((file: FCFileGridItem) =>
                isEmptyInstance(file?.sizeNotAvailable)
              ),
              ...this.uploadedFileGridItemArray
                .filter((file: FCFileGridItem) => file?.sizeNotAvailable)
                .map((item) => ({
                  ...item,
                  size:
                    localFileNameSizeMap.get(item?.remoteFilename ?? '') ?? 0,
                  sizeNotAvailable: undefined,
                })),
            ],
            'id'
          ),
          'remoteFilename'
        );

        this.onUpdateUploadedFileArray?.(
          this.uploadTaskId,
          this.uploadedFileGridItemArray
        );

        this.updateUploadedGridItemUploadSessionId();
      });
    } else {
      this.uploadedFileGridItemArray = uniqBy(
        uniqBy(this.uploadedFileGridItemArray, 'id'),
        'remoteFilename'
      );

      this.onUpdateUploadedFileArray?.(
        this.uploadTaskId,
        this.uploadedFileGridItemArray
      );

      this.updateUploadedGridItemUploadSessionId();
    }
  }

  updateUploadedGridItemUploadSessionId() {
    if (
      this.uploadedFileGridItemArray.some((file: FCFileGridItem) =>
        isEmptyInstance(file?.uploadSessionId)
      ) &&
      !isEmptyInstance(this.clientId) &&
      !isEmptyInstance(this.projectId)
    ) {
      const localFileNameUploadSessionIdMap: Map<string, string> = new Map();

      Promise.allSettled(
        this.uploadedFileGridItemArray
          .filter(
            (file: FCFileGridItem) =>
              isEmptyInstance(file?.uploadSessionId) && (file?.size ?? 0) > 0
          )
          .map(async (file: FCFileGridItem) => {
            const uploadSessionData: UploadSessionData | undefined =
              await this.getUploadFileSessionId(file);

            localFileNameUploadSessionIdMap.set(
              file?.localDirectoryPath ?? '',
              uploadSessionData?.uploadSessionId ?? ''
            );
          })
      ).then((results: PromiseSettledResult<void>[]) => {
        this.uploadedFileGridItemArray = uniqBy(
          uniqBy(
            [
              ...this.uploadedFileGridItemArray.filter(
                (file: FCFileGridItem) =>
                  !isEmptyInstance(file?.uploadSessionId)
              ),
              ...this.uploadedFileGridItemArray
                .filter((file: FCFileGridItem) =>
                  isEmptyInstance(file?.uploadSessionId)
                )
                .map((item) => ({
                  ...item,
                  uploadSessionId:
                    localFileNameUploadSessionIdMap.get(
                      item?.localDirectoryPath ?? ''
                    ) ?? '',
                })),
            ],
            'id'
          ),
          'remoteFilename'
        );

        this.onUpdateUploadedFileArray?.(
          this.uploadTaskId,
          this.uploadedFileGridItemArray
        );

        !isEmptyInstance(this.onFCError) &&
          results
            .filter(
              (result: PromiseSettledResult<void>) =>
                result.status === 'rejected'
            )
            .map(
              (result: PromiseSettledResult<void>) =>
                (result as PromiseRejectedResult).reason
            )
            .forEach((error) => this.onFCError?.(error));
      });
    }
  }

  deleteRemoteFile(
    connectionKey: string,
    fileGridItem: FCFileGridItem,
    callback?: (success: boolean) => void
  ) {
    const {remoteFilename = ''} = fileGridItem ?? {};
    deleteRemoteFile(connectionKey, remoteFilename)
      .then((response) => {
        if (response.status === HttpStatusCode.Ok) {
          const {remoteFileNames = new Set([])} = this.fileTransferSession;

          const updatedRemoteFileNameArray = Array.from(remoteFileNames).filter(
            (item: string) => item !== remoteFilename
          );

          this.fileTransferSession = {
            ...this.fileTransferSession,
            remoteFileNames: new Set(updatedRemoteFileNameArray),
          };

          this.uploadedFileGridItemArray = (
            this.uploadedFileGridItemArray ?? []
          ).filter(
            (item: FCFileGridItem) => item?.remoteFilename !== remoteFilename
          );

          this.onUpdateUploadedFileArray?.(
            this.uploadTaskId,
            this.uploadedFileGridItemArray
          );

          callback?.(true);
        } else {
          callback?.(false);
        }
      })
      .catch((e) => {
        console.error(e);
        this.isRemoteFileDeleted = false;
        this.onFCError?.(e);
        callback?.(false);
      });
  }

  async getUploadFileSessionId(file: FCFileGridItem, signal?: AbortSignal) {
    try {
      const fileName = getFileNameFromRemotePath(
        this.remoteDirectoryBasePath,
        file?.name ?? ''
      );

      const mimeType = getMimeTypeFromFileName(fileName);
      const storagePath = getStoragePathParam(
        this.remoteDirectoryBasePath ?? ''
      );

      const payload = {
        fileName,
        fileSize: file?.size ?? 0,
        uploadMethod: UploadMethod.HighSpeed,
        mimeType,
        storagePath,
      };

      const response = await createFCUploadFileSessionId(
        this.clientId,
        this.projectId,
        this.evidenceSourceSessionId,
        payload,
        signal
      );

      return response.data as unknown as UploadSessionData;
    } catch (e) {
      this.onFCError?.(e as AxiosError);
      return undefined;
    }
  }

  removeUploadedFileFromArray(remoteFilename: string) {
    if (
      this.uploadedFileGridItemArray.some(
        (item) => item?.remoteFilename === remoteFilename
      )
    ) {
      const {remoteFileNames = new Set([])} = this.fileTransferSession;

      const updatedRemoteFileNameArray = Array.from(remoteFileNames).filter(
        (item: string) => item !== remoteFilename
      );

      this.fileTransferSession = {
        ...this.fileTransferSession,
        remoteFileNames: new Set(updatedRemoteFileNameArray),
      };

      this.uploadedFileGridItemArray = (
        this.uploadedFileGridItemArray ?? []
      ).filter(
        (item: FCFileGridItem) => item?.remoteFilename !== remoteFilename
      );

      this.onUpdateUploadedFileArray?.(
        this.uploadTaskId,
        this.uploadedFileGridItemArray
      );
    }
  }

  updatePendingGridItemToCustomStatus(
    status: FCFileTransferState = FCFileTransferState.FAILED
  ) {
    this.pendingFileGridItemArray = (this.pendingFileGridItemArray ?? []).map(
      (item: FCFileGridItem) => ({...item, status})
    );
    this.onUpdatePendingFileArray?.(
      this.uploadTaskId,
      this.pendingFileGridItemArray
    );
  }

  updatePendingGridItemToReconnectingStatus() {
    this.fileTransferInternalState = FCFileTransferInternalState.RECONNECTING;
    this.fileTransferState = FCFileTransferState.RECONNECTING;
    this.updatePendingGridItemToCustomStatus(FCFileTransferState.RECONNECTING);
  }

  setFailState() {
    if (!this.isUploadTaskCancelled) {
      this.fileTransferState = FCFileTransferState.FAILED;
      this.updatePendingGridItemToCustomStatus();
      this.onTransferFailed?.(this.uploadTaskId);
    }
  }

  cancelFileTransfer(fileGridItem: FCFileGridItem) {
    return new Promise((resolve) => {
      const {transferID = '', id = '', remoteFilename = ''} = fileGridItem;
      if (isEmptyInstance(remoteFilename)) {
        this.isUploadTaskCancelled = true;

        this.pendingFileGridItemArray = this.pendingFileGridItemArray?.filter(
          (item: FCFileGridItem) => item?.id !== id
        );

        this.onUpdatePendingFileArray?.(
          this.uploadTaskId,
          this.pendingFileGridItemArray
        );

        this.fileTransferState = FCFileTransferState.CANCELLED;
        this.fileTransferInternalState =
          FCFileTransferInternalState.HARD_CANCELLED;
        this.onTransferCompleted?.(this.uploadTaskId);
        resolve(true);
      } else if (fileGridItem.status !== FCFileTransferState.COMPLETED) {
        this.isUploadTaskCancelled = true;

        fileGridItem.status !== FCFileTransferState.RECONNECTING &&
          this.abortController?.abort();
        this.pendingFileGridItemArray = this.pendingFileGridItemArray.map(
          (item) => ({...item, isTranferCancelled: true})
        );

        this.onUpdatePendingFileArray?.(
          this.uploadTaskId,
          this.pendingFileGridItemArray.slice()
        );

        cancelFileTransfer(transferID)
          .then((response) => {
            if (
              [HttpStatusCode.Ok, HttpStatusCode.NotFound].includes(
                response.status
              )
            ) {
              this.fileTransferState = FCFileTransferState.CANCELLED;
              this.fileTransferInternalState =
                FCFileTransferInternalState.SOFT_CANCELLED;

              this.pendingFileGridItemArray = [];
              this.onUpdatePendingFileArray?.(
                this.uploadTaskId,
                this.pendingFileGridItemArray
              );
              this.onTransferCompleted?.(this.uploadTaskId);
              resolve(true);
            } else {
              this.isUploadTaskCancelled = false;
              this.pendingFileGridItemArray = this.pendingFileGridItemArray.map(
                (item) => ({
                  ...item,
                  isTranferCancelled: false,
                })
              );
              this.onUpdatePendingFileArray?.(
                this.uploadTaskId,
                this.pendingFileGridItemArray.slice()
              );
              resolve(false);
            }
          })
          .catch((e) => {
            console.error(`updateCancelledPendingGridItem: `, e);
            this.isUploadTaskCancelled = false;
            this.pendingFileGridItemArray = this.pendingFileGridItemArray.map(
              (item) => ({...item, isTranferCancelled: false})
            );
            this.onUpdatePendingFileArray?.(
              this.uploadTaskId,
              this.pendingFileGridItemArray.slice()
            );
            this.abortController?.abort();
            this.onFCError?.(e);
            resolve(false);
          });
      }
    });
  }
}

export default FCUploadFileTask;
